import { useState } from 'react';
import { ethers } from 'ethers';
import { useNavigate } from 'react-router-dom';
import Page from '../components/Page';
import Crumbs from '../components/Crumbs';
import { useAccount } from '../hooks/useWallet';
import { useDefaultRequestDeposit } from '../hooks/useAuditingPoolConfig';
import { useFirmIdOf, NOT_FOUND, MILESTONE_TYPES } from '../hooks/useAuditorRegistry';
import { useWriteContract } from '../hooks/useWriteContract';
import { weiToEth } from '../lib/format';

// MilestoneType enum index 3 == ClinicalData (see AuditorRegistryV2.sol L85+).
// Pull from MILESTONE_TYPES rather than hard-coding so a future enum reshuffle
// stays in sync.
const CLINICAL_DATA_IDX = MILESTONE_TYPES.indexOf('ClinicalData');

type Milestone = { name: string; eth: string; weeks: string; mtype: number };

const INITIAL: Milestone[] = [
  { name: 'Biomarker isolation',     eth: '0.020', weeks: '4',  mtype: 1 /* Scientific */ },
  { name: 'Animal model validation', eth: '0.025', weeks: '6',  mtype: 1 /* Scientific */ },
  { name: 'Cohort recruitment',      eth: '0.015', weeks: '8',  mtype: 3 /* ClinicalData */ },
  { name: 'Clinical assay',          eth: '0.040', weeks: '10', mtype: 3 /* ClinicalData */ },
  { name: 'Validation cohort',       eth: '0.060', weeks: '12', mtype: 3 /* ClinicalData */ },
  { name: 'Publication',             eth: '0.020', weeks: '6',  mtype: 6 /* Operational */ },
];

export default function NewRequest() {
  const account = useAccount();
  const navigate = useNavigate();
  const [milestones, setMilestones] = useState<Milestone[]>(INITIAL);
  const [accepted, setAccepted] = useState(true);
  const [title, setTitle] = useState('A novel troponin biomarker for early MI detection');
  const [description, setDescription] = useState('Identification and validation of a new troponin variant for accelerated detection of myocardial infarction in emergency settings.');
  const [ipfsHash, setIpfsHash] = useState('Qm…3aF');
  // §3.3 — when ON, milestone N≥1 stays Locked until the Program Officer
  // calls exerciseOption(reqId, N). M0 still auto-unlocks at acceptVerdict.
  const [bardaOption, setBardaOption] = useState(false);

  const { data: defaultDepBn } = useDefaultRequestDeposit();
  const myFirmId = useFirmIdOf(account);
  const isFirmWallet = myFirmId.data !== undefined && myFirmId.data !== NOT_FOUND;
  const submissionDepEth = defaultDepBn !== undefined ? weiToEth(defaultDepBn) : 0.005;
  const submissionDepWei = defaultDepBn ?? ethers.parseEther('0.005');

  const { write, status } = useWriteContract();
  const submitting = status === 'submitting' || status === 'pending';

  const totalEth = milestones.reduce((s, m) => s + (parseFloat(m.eth) || 0), 0);
  const totalWeeks = milestones.reduce((s, m) => s + (parseInt(m.weeks) || 0), 0);
  const hasValidMilestones = milestones.length > 0 && milestones.every((m) => m.name.trim() && parseFloat(m.eth) > 0);
  const canSubmit = !!account && accepted && hasValidMilestones && title.trim().length > 0 && !submitting;

  // §3.2 — at least one ClinicalData milestone implies DSMB veto applies and we
  // must commit hasClinical=true. The contract validates this internally too.
  const hasClinical = milestones.some((m) => m.mtype === CLINICAL_DATA_IDX);
  // Decide which contract entrypoint to invoke based on what the user filled in:
  //   - any non-Generic type OR clinical lane              → submitRequestTyped
  //   - bardaOption ON                                      → submitRequestWithOptions
  //   - otherwise                                           → plain submitRequest
  const hasTypedMilestones = milestones.some((m) => m.mtype !== 0);
  const submitFn =
    bardaOption                                   ? 'submitRequestWithOptions' :
    (hasTypedMilestones || hasClinical)           ? 'submitRequestTyped' :
    /* fully generic, classic flow */               'submitRequest';

  const update = (i: number, patch: Partial<Milestone>) =>
    setMilestones((arr) => arr.map((m, j) => (i === j ? { ...m, ...patch } : m)));
  const remove = (i: number) =>
    setMilestones((arr) => arr.filter((_, j) => j !== i));
  const add = () =>
    setMilestones((arr) => [...arr, { name: '', eth: '0.000', weeks: '0', mtype: 0 }]);

  async function submit() {
    if (!canSubmit) return;
    let amountsWei: bigint[];
    try {
      amountsWei = milestones.map((m) => ethers.parseEther(m.eth || '0'));
    } catch {
      return; // parse error — silently ignore for now
    }
    const names = milestones.map((m) => m.name.trim());
    const types = milestones.map((m) => m.mtype);

    // submitRequest             (title, desc, ipfs, names[], amounts[])
    // submitRequestTyped        (title, desc, ipfs, names[], amounts[], types[], hasClinical)
    // submitRequestWithOptions  (title, desc, ipfs, names[], amounts[], types[], hasClinical)
    const baseArgs: unknown[] = [title.trim(), description.trim(), ipfsHash.trim() || 'Qm', names, amountsWei];
    const args: unknown[] =
      submitFn === 'submitRequest'
        ? baseArgs
        : [...baseArgs, types, hasClinical];

    const result = await write({
      contract: 'auditingPool',
      fn: submitFn,
      args,
      options: {
        value: submissionDepWei,
        pendingTitle: 'Submitting research request…',
        successTitle: `Request "${title.trim()}" submitted`,
        invalidate: [['metha', 'auditingPool']],
      },
    });
    if (result) {
      // Send the user to the auditing list so they can see their fresh request.
      navigate('/auditing');
    }
  }
  void isFirmWallet; // referenced for future cross-role validation

  return (
    <Page ctxLabel="new request" ctx="lab" title="Metha · New request" docsPath="guides/lab" docsLabel="Lab guide">
      <Crumbs crumbs={[
        { to: '/', label: 'Overview' },
        { to: '/auditing', label: 'Auditing' },
        { label: 'New request' },
      ]} />

      <div className="ph">
        <div>
          <h1>Submit research request <span className="acc">/ as lab</span></h1>
          <p className="lede">
            A research proposal goes on-chain via{' '}
            <span className="mono" style={{ fontSize: 11 }}>submitRequest()</span> with a 0.005 ETH
            anti-spam deposit. Auditors then assess it; on approval, milestone 1 unlocks and you
            can claim the first tranche.
          </p>
        </div>
        <div className="meta">
          <span>DEPOSIT <b>0.005 ETH</b></span>
          <span>EST. GAS <b>0.0021 ETH</b></span>
        </div>
      </div>

      <div className="steps">
        <span className="step done"><span className="n">1</span>Identity</span>
        <span className="arr">→</span>
        <span className="step curr"><span className="n">2</span>Proposal</span>
        <span className="arr">→</span>
        <span className="step"><span className="n">3</span>Milestones</span>
        <span className="arr">→</span>
        <span className="step"><span className="n">4</span>Review &amp; submit</span>
      </div>

      <div className="row r-2u1 mb-14">
        <div>
          <div className="panel mb-14">
            <div className="p-head"><h3>Proposal</h3><span className="src"><code>submitRequest(...)</code></span></div>
            <div className="p-body">
              <div className="form-row r2">
                <div>
                  <span className="field-l">Lab wallet (msg.sender)</span>
                  <div className="input"><input value={account ?? ''} placeholder="connect wallet" readOnly /></div>
                  <p className="help">This is the address that will receive milestone disbursements. You can transfer ownership later via governance.</p>
                </div>
                <div>
                  <span className="field-l">Domain</span>
                  <div className="input"><input defaultValue="cardiology · troponin biomarkers" /></div>
                  <p className="help">Free-text. Helps auditors with the right specialization apply.</p>
                </div>
              </div>

              <div className="form-row">
                <span className="field-l">Title (string, on-chain)</span>
                <div className="input"><input value={title} onChange={(e) => setTitle(e.target.value)} /></div>
              </div>

              <div className="form-row">
                <span className="field-l">Short description (string, on-chain)</span>
                <textarea className="ta" value={description} onChange={(e) => setDescription(e.target.value)} />
              </div>

              <div className="form-row">
                <span className="field-l">Full proposal (IPFS hash)</span>
                <div className="input"><input value={ipfsHash} onChange={(e) => setIpfsHash(e.target.value)} placeholder="Qm… (auto-fills after upload)" /></div>
                <p className="help">
                  Upload your PDF/Markdown to IPFS — only the bytes32 hash goes on-chain.{' '}
                  <a href="#" className="acc" onClick={(e) => { e.preventDefault(); alert('mock · open IPFS uploader'); }}>Upload now ↗</a>
                </p>
              </div>

              <div className="form-row r2">
                <div>
                  <span className="field-l">Cover image (optional, off-chain via Supabase)</span>
                  <div className="input"><input placeholder="paste URL or upload" /></div>
                </div>
                <div>
                  <span className="field-l">Team (optional, off-chain via Supabase)</span>
                  <div className="input"><input placeholder="JSON of team_members" /></div>
                </div>
              </div>
            </div>
          </div>

          <div className="panel mb-14">
            <div className="p-head">
              <h3>Milestone schedule</h3>
              <span className="src"><code>{submitFn}(...)</code></span>
            </div>
            <div className="tbl">
              <div className="th" style={{ gridTemplateColumns: '40px 1fr 110px 80px 130px 40px' }}>
                <span>#</span><span>NAME</span><span>AMOUNT (ETH)</span><span>WEEKS</span><span>TYPE</span><span></span>
              </div>
              {milestones.map((m, i) => (
                <div key={i} className="tr" style={{ gridTemplateColumns: '40px 1fr 110px 80px 130px 40px', cursor: 'default' }}>
                  <span className="pid">M{i + 1}</span>
                  <span><div className="input" style={{ background: 'transparent', borderColor: 'var(--line)' }}>
                    <input value={m.name} onChange={(e) => update(i, { name: e.target.value })} />
                  </div></span>
                  <span><div className="input" style={{ background: 'transparent', borderColor: 'var(--line)' }}>
                    <input value={m.eth} onChange={(e) => update(i, { eth: e.target.value })} />
                  </div></span>
                  <span><div className="input" style={{ background: 'transparent', borderColor: 'var(--line)' }}>
                    <input value={m.weeks} onChange={(e) => update(i, { weeks: e.target.value })} />
                  </div></span>
                  <span>
                    <div className="input" style={{ background: 'transparent', borderColor: 'var(--line)' }}>
                      <select
                        value={m.mtype}
                        onChange={(e) => update(i, { mtype: Number(e.target.value) })}
                        style={{ width: '100%', background: 'transparent', color: 'var(--ink)', border: 'none', outline: 'none', fontFamily: 'inherit', fontSize: 'inherit' }}
                        title="MilestoneType enum (AuditorRegistryV2.sol L85)"
                      >
                        {MILESTONE_TYPES.map((t, idx) => (
                          <option key={t} value={idx} style={{ background: 'var(--panel)', color: 'var(--ink)' }}>{t}</option>
                        ))}
                      </select>
                    </div>
                  </span>
                  <span><button className="btn btn-sm" onClick={() => remove(i)} title="Remove milestone">×</button></span>
                </div>
              ))}
            </div>
            <div style={{ padding: '12px 16px', borderTop: '1px solid var(--line)', display: 'flex', justifyContent: 'space-between', alignItems: 'center', fontFamily: 'var(--mono)', fontSize: 12 }}>
              <span className="dim">
                TOTAL <b style={{ color: 'var(--ink)', marginLeft: 6 }}>{totalEth.toFixed(3)} ETH</b>{' '}
                · {totalWeeks} weeks · {milestones.length} milestones
                {hasClinical && (
                  <>
                    {' '}· <span style={{ color: 'var(--amber)' }}>hasClinical=true</span> · DSMB veto active
                  </>
                )}
              </span>
              <button className="btn btn-sm" onClick={add}>+ Add milestone</button>
            </div>
          </div>

          {/* §3.3 — BARDA option-period gating toggle */}
          <div className="panel mb-14">
            <div className="p-head">
              <h3>Option-period gating <span className="dim" style={{ fontWeight: 400, fontSize: 11, marginLeft: 8 }}>§3.3 · BARDA-style</span></h3>
              <span className="src"><code>{bardaOption ? 'submitRequestWithOptions(...)' : 'submitRequestTyped(...)'}</code></span>
            </div>
            <div className="p-body">
              <label className="mono" style={{ fontSize: 11, letterSpacing: 0.3, display: 'flex', alignItems: 'flex-start', gap: 8 }}>
                <input
                  type="checkbox"
                  style={{ accentColor: 'var(--ctx)', marginTop: 2 }}
                  checked={bardaOption}
                  onChange={(e) => setBardaOption(e.target.checked)}
                />
                <span>
                  Each milestone <b>N≥1</b> stays <b>Locked</b> after N−1 is Approved, until the
                  Program Officer (this contract's owner — typically OpenGovernance post-deploy)
                  calls <span className="mono">exerciseOption(reqId, N)</span>. Milestone 0 still
                  auto-unlocks at <span className="mono">acceptVerdict</span> (analogue to BARDA's
                  base period being executed at award).
                </span>
              </label>
            </div>
          </div>

          <div className="warn mb-14">
            <strong>Settlement clause acknowledgement (cond. a · planned).</strong>{' '}
            By submitting this request you accept that, once condition (a) is activated by holder
            vote (phase 3), licensing of IP derived from this research will be denominated in METH.
            The clause will be recorded with the relevant patent offices as an encumbrance binding
            subsequent purchasers.
            <div style={{ marginTop: 8 }}>
              <label className="mono" style={{ fontSize: 11, letterSpacing: 0.3 }}>
                <input type="checkbox" style={{ verticalAlign: 'middle', marginRight: 6, accentColor: 'var(--ctx)' }}
                       checked={accepted} onChange={(e) => setAccepted(e.target.checked)} />
                I have read and accept the settlement clause.
              </label>
            </div>
          </div>
        </div>

        <aside>
          <div className="panel mb-14">
            <div className="p-head"><h3>Submission summary</h3><span className="src">to be written on-chain</span></div>
            <div className="p-body">
              <div className="estim"><span>TOTAL BUDGET</span><b>{totalEth.toFixed(3)} ETH</b></div>
              <div className="estim"><span>MILESTONES</span><b>{milestones.length}</b></div>
              <div className="estim"><span>DURATION</span><b>{totalWeeks} weeks</b></div>
              <div className="estim"><span>SUBMISSION DEPOSIT</span><b>{submissionDepEth} ETH</b></div>
              <div className="estim"><span>METH BOND</span><b>~ {(totalEth * 0.10).toFixed(3)} METH (phase 2)</b></div>
              <div className="estim"><span>EST. GAS</span><b>0.0021 ETH</b></div>
              <div className="estim" style={{ borderTop: '1px solid var(--line-2)', marginTop: 8, paddingTop: 10 }}>
                <span>TOTAL TO PAY NOW</span>
                <b style={{ color: 'var(--ctx)', fontSize: 13 }}>0.0071 ETH</b>
              </div>

              <button className={`btn btn-prim btn-block btn-lg mt-14${canSubmit ? '' : ' btn-locked'}`}
                      disabled={!canSubmit}
                      onClick={submit}>
                {submitting ? 'Submitting…' :
                  !account ? 'Connect wallet to submit' :
                    !accepted ? 'Accept settlement clause' :
                      !title.trim() ? 'Add a title' :
                        !hasValidMilestones ? 'Each milestone needs a name and amount' :
                          `Submit request (${submissionDepEth} ETH deposit)`}
              </button>
              <button className="btn btn-ghost btn-block mt-14"
                      onClick={() => alert('mock · save draft to local + Supabase')}>
                Save as draft
              </button>
              <p className="help mt-14">
                Once submitted, your request appears in the{' '}
                <a href="/auditing" className="acc">auditing list</a>. Auditors with relevant
                domains may apply via <span className="mono">applyForAudit()</span>; multi-auditor
                voting then determines verdict.
              </p>
            </div>
          </div>

          <div className="panel">
            <div className="p-head"><h3>What happens next</h3><span className="src">milestone state machine</span></div>
            <div className="p-body">
              <div className="fsm" style={{ flexDirection: 'column', alignItems: 'flex-start', gap: 6 }}>
                <span className="st curr">Submission accepted</span>
                <span className="st">→ auditors apply</span>
                <span className="st">→ multi-auditor cast vote</span>
                <span className="st">→ verdict averaged</span>
                <span className="st">→ you accept verdict</span>
                <span className="st">→ M1 unlocks</span>
                <span className="st">→ claim → submit proof → next stage</span>
              </div>
              <p className="help mt-14">
                If verdict rejects you, your 0.005 ETH deposit is returnable via{' '}
                <span className="mono">claimRequestDeposit()</span>. If you cancel, the deposit is
                donated to the vault (<span className="mono">burnRequestDeposit()</span>).
              </p>
            </div>
          </div>
        </aside>
      </div>
    </Page>
  );
}
